import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { useSeo } from "@/hooks/useSeo";
import { channels, channelTopics } from "@/mocks/channels";

export default function Channel() {
  useSeo({
    title: "频道推荐 - 黑桃视频影视频道专题",
    description:
      "黑桃视频频道推荐专题聚合优质影视频道，涵盖院线大片、热播剧集、文艺电影、纪录影像等，帮助你发现高质量观看源与内容专题。",
    keywords: "影视频道,频道推荐,黑桃视频,影视专题,观看指南,影视聚合",
  });

  return (
    <Layout>
      <section className="relative h-[280px] w-full overflow-hidden md:h-[380px]">
        <img
          src="https://readdy.ai/api/search-image?query=cinematic%20dark%20home%20theater%20wall%20with%20multiple%20screens%20glowing%20warm%20amber%2C%20premium%20media%20collection%20atmosphere%2C%20wide%20banner%2C%20high%20detail&width=1600&height=700&seq=ht-ch-hero&orientation=landscape"
          alt="黑桃视频频道推荐专题主图"
          title="影视频道推荐 - 黑桃视频"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/60 to-background-950/30"></div>
        <div className="absolute inset-0 flex w-full items-end">
          <div className="mx-auto w-full max-w-[1440px] px-4 pb-8 md:px-8 md:pb-12">
            <span className="inline-block rounded-full bg-primary-500 px-3 py-1 text-[11px] font-500 text-background-50">
              频道专题
            </span>
            <h1 className="mt-3 font-heading text-2xl font-700 tracking-wide text-background-950 md:text-4xl">
              热门影视频道推荐
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-foreground-800 md:text-base">
              精选六大主题影视频道，覆盖院线大片、热播剧集、文艺电影、纪录影像与经典修复，帮你快速找到想看的内容方向。
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8">
        <h2 className="font-heading text-xl font-600 tracking-wide text-foreground-950">全部频道</h2>
        {channels.length === 0 ? (
          <p className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-12 text-center text-sm text-foreground-500">
            频道内容暂时更新中
          </p>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {channels.map((channel) => (
              <Link
                key={channel.id}
                to={`/channel/${channel.id}`}
                className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-100 transition-colors hover:border-accent-400"
              >
                <div className="relative h-[180px] w-full overflow-hidden bg-background-200">
                  <img
                    src={channel.cover}
                    alt={`${channel.name} 频道封面`}
                    title={`${channel.name} - 黑桃视频影视频道推荐`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded bg-background-950/70 px-2 py-0.5 text-[11px] font-500 text-accent-400 backdrop-blur">
                    {channel.stats}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-heading text-lg font-600 text-foreground-950 transition-colors group-hover:text-accent-500">
                    {channel.name}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500">{channel.short}</p>
                  <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-700">
                    {channel.intro}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {channel.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <section className="mt-14">
          <h2 className="flex items-center gap-2 font-heading text-xl font-600 tracking-wide text-foreground-950">
            <span className="flex h-6 w-6 items-center justify-center text-primary-500">
              <i className="ri-thumb-up-line text-[20px]" aria-hidden="true"></i>
            </span>
            推荐理由
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500/15 text-primary-500">
                <i className="ri-filter-3-line text-[22px]" aria-hidden="true"></i>
              </span>
              <h3 className="mt-3 font-500 text-foreground-950">严选内容标准</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                每个频道都由编辑团队按口碑、题材与制作水准筛选，减少无效浏览。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500/20 text-accent-500">
                <i className="ri-time-line text-[22px]" aria-hidden="true"></i>
              </span>
              <h3 className="mt-3 font-500 text-foreground-950">更新节奏清晰</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                频道标注更新频率与内容体量，方便你安排追更与观影计划。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                <i className="ri-compass-3-line text-[22px]" aria-hidden="true"></i>
              </span>
              <h3 className="mt-3 font-500 text-foreground-950">发现新方向</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                从熟悉的类型出发，探索平时容易忽略的题材与影像风格。
              </p>
            </div>
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-heading text-xl font-600 tracking-wide text-foreground-950">相关专题推荐</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
            {channelTopics.map((topic) => (
              <Link
                key={topic.id}
                to={topic.link}
                className="group flex items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-primary-400"
              >
                <div className="min-w-0">
                  <h3 className="text-sm font-500 text-foreground-950 transition-colors group-hover:text-primary-500">
                    {topic.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-foreground-600">{topic.desc}</p>
                </div>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background-200 text-foreground-700 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className="ri-arrow-right-line text-[18px]" aria-hidden="true"></i>
                </span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}